import Button from "@/Components/Button/Button";
import RteIcon from "@/Components/RichTextEditor/RteIcon";
import Spinner from "@/Components/Spinner/Spinner";
import PollStatusLabel from "@/Pages/Auth/Admin/Polls/PollStatusLabel";
import usePollVotePost from "@/shared/mutations/polls/usePollVotePost";
import usePollResultsQuery from "@/shared/queries/polls/usePollResultsQuery";
import { cn } from "@/shared/utils/cn";
import { formatDate } from "@/shared/utils/helpers";
import { PollStatus } from "@/shared/utils/interfaces";
import { getPollStatus } from "@/shared/utils/polls";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const PollCard = ({ children }: { children: ReactNode }) => (
  <div className="rounded-2xl border border-border bg-white p-6 text-text shadow-sm md:p-8">
    {children}
  </div>
);

// Grows from zero on mount so the result reads as an animation after voting
const ResultBar = ({ percentage, mine }: { percentage: number; mine: boolean }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setWidth(percentage));
    return () => cancelAnimationFrame(frame);
  }, [percentage]);

  return (
    <div
      className={cn(
        "absolute inset-y-0 left-0 transition-[width] duration-700 ease-out",
        mine ? "bg-primary/20" : "bg-light/50"
      )}
      style={{ width: `${width}%` }}
    />
  );
};

interface PollProps {
  pollId: number;
  // Shows the poll read only, as in the message editor
  preview?: boolean;
}

const Poll = ({ pollId, preview = false }: PollProps) => {
  const { t } = useTranslation("", { keyPrefix: "pollVote" });
  const query = usePollResultsQuery(pollId);
  const vote = usePollVotePost();
  const [selected, setSelected] = useState<number[]>([]);

  if (query.isPending) {
    return (
      <PollCard>
        <div className="flex items-center gap-3 text-text-tertiary">
          <Spinner size="0.6rem" />
          {t("loading")}
        </div>
      </PollCard>
    );
  }

  // A deleted poll quietly disappears for customers, admins get told
  if (query.isError || !query.data) {
    return preview ? (
      <PollCard>
        <div className="flex items-center gap-3 text-text-tertiary">
          <RteIcon name="poll" />
          {t("unavailable")}
        </div>
      </PollCard>
    ) : null;
  }

  const { poll, results, total_responses, my_option_ids } = query.data;
  const status = getPollStatus(poll);
  const multiple = poll.can_choose_multiple_options;
  const hasVoted = my_option_ids.length > 0;
  const canVote = !preview && status === PollStatus.ACTIVE && !hasVoted;
  const showResults = preview || hasVoted || status === PollStatus.ENDED;

  const votesFor = (optionId: number) =>
    results.find((result) => result.option_id === optionId)?.votes ?? 0;

  const toggle = (optionId: number) =>
    setSelected((current) => {
      if (!multiple) return [optionId];
      return current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
    });

  const submit = () =>
    vote.mutate(
      { pollId, optionIds: selected },
      { onSuccess: () => setSelected([]) }
    );

  const footerText = (() => {
    if (status === PollStatus.SCHEDULED)
      return t("opensAt", { date: formatDate(poll.start_time, "dateTime") });
    if (status === PollStatus.ENDED)
      return t("endedAt", { date: formatDate(poll.end_time, "dateTime") });
    if (hasVoted) return t("thanks");
    return t("endsAt", { date: formatDate(poll.end_time, "dateTime") });
  })();

  return (
    <PollCard>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-small font-semibold text-primary">
          <RteIcon name="poll" className="h-[1.4rem] w-[1.4rem]" />
          {t("label")}
        </span>
        <PollStatusLabel status={status} />
      </div>

      <div className="text-[1.8rem] font-semibold leading-snug">
        {poll.question}
      </div>
      {poll.description && (
        <div className="mt-1 text-small text-text-secondary">
          {poll.description}
        </div>
      )}
      {canVote && (
        <div className="mt-3 text-small text-text-tertiary">
          {multiple ? t("chooseMultiple") : t("chooseOne")}
        </div>
      )}

      <div
        className="mt-5 flex flex-col gap-3"
        role={showResults ? undefined : multiple ? "group" : "radiogroup"}
      >
        {poll.options.map((option) => {
          if (showResults) {
            const votes = votesFor(option.item_id);
            // Share of respondents, with multiple options these add up past 100
            const percentage = total_responses
              ? Math.round((votes / total_responses) * 100)
              : 0;
            const mine = my_option_ids.includes(option.item_id);

            return (
              <div
                key={option.item_id}
                className={cn(
                  "relative overflow-hidden rounded-xl border px-5 py-4",
                  mine ? "border-primary" : "border-border"
                )}
                title={t("votes", { count: votes })}
              >
                <ResultBar percentage={percentage} mine={mine} />
                <div className="relative flex items-center gap-3">
                  <span className="min-w-0 flex-1 font-medium">
                    {option.text}
                  </span>
                  {mine && (
                    <span className="flex items-center gap-1 text-small font-semibold text-primary">
                      <RteIcon name="check" className="h-[1.4rem] w-[1.4rem]" />
                      {t("yourVote")}
                    </span>
                  )}
                  <span className="w-[4.5rem] text-right font-semibold tabular-nums">
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          }

          const isSelected = selected.includes(option.item_id);
          return (
            <button
              key={option.item_id}
              type="button"
              role={multiple ? "checkbox" : "radio"}
              aria-checked={isSelected}
              disabled={!canVote}
              onClick={() => toggle(option.item_id)}
              className={cn(
                "flex w-full items-center gap-4 rounded-xl border-2 bg-transparent px-5 py-4 text-left text-text transition-colors",
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-border",
                canVote
                  ? "cursor-pointer hover:border-primary/60"
                  : "cursor-default opacity-70"
              )}
            >
              <span
                className={cn(
                  "flex h-[2rem] w-[2rem] shrink-0 items-center justify-center border-2 transition-colors",
                  multiple ? "rounded-md" : "rounded-full",
                  isSelected ? "border-primary bg-primary text-white" : "border-border"
                )}
              >
                {isSelected &&
                  (multiple ? (
                    <RteIcon
                      name="check"
                      strokeWidth={3}
                      className="h-[1.3rem] w-[1.3rem]"
                    />
                  ) : (
                    <span className="h-[0.8rem] w-[0.8rem] rounded-full bg-white" />
                  ))}
              </span>
              <span className="min-w-0 flex-1 font-medium">{option.text}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <span className="text-small text-text-tertiary">
          {showResults && `${t("responses", { count: total_responses })} · `}
          {footerText}
        </span>
        {canVote && (
          <Button
            type="button"
            size="small"
            disabled={!selected.length}
            isDisabled={!selected.length}
            isPending={vote.isPending}
            onClick={submit}
          >
            {t("vote")}
          </Button>
        )}
      </div>
    </PollCard>
  );
};

export default Poll;
